import { ChangeDetectorRef, Component, ElementRef, Input, OnInit } from '@angular/core'
import { DropdownSettings } from 'angular2-multiselect-dropdown/lib/multiselect.interface'
import { TranslocoService } from '@ngneat/transloco'
import { FormControl } from '@angular/forms'
import { BehaviorSubject, tap, zip } from 'rxjs'

@Component({
  selector: 'app-multiselect',
  templateUrl: './multiselect.component.html'
})
export class MultiselectComponent implements OnInit {
  @Input({ required: true }) data: MultiSelectData;
  @Input() counterOnly = false;
  @Input() disabled = false;
  @Input() position: 'top' | 'bottom';
  @Input() popupMaxWidth: number;
  @Input() badgeLimit: number;
  @Input() enableCheckAll: boolean;
  @Input() placeholder: string;
  @Input() translateLabels = true;

  private loadedSubject = new BehaviorSubject<boolean>(false);
  protected loaded$ = this.loadedSubject.asObservable();
  protected arrowDownwards = true;
  protected arrowUpwards = false;
  protected readonly settings: DropdownSettings = {
    enableCheckAll: true,
    primaryKey: 'id',
    labelKey: 'label',
    tagToBody: false,
    maxHeight: 400
  };

  constructor(private readonly translocoService: TranslocoService,
              private readonly elementRef: ElementRef,
              private readonly cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    if (this.counterOnly) {
      this.settings.badgeShowLimit = 1;
    }
    if (this.position) {
      this.settings.position = this.position;
      this.settings.autoPosition = false;
    }
    if (this.badgeLimit) {
      this.settings.badgeShowLimit = this.badgeLimit;
    }
    if (this.enableCheckAll === false) {
      this.settings.enableCheckAll = false;
    }
    this.elementRef.nativeElement.style.setProperty('--popup-max-width', this.popupMaxWidth ? this.popupMaxWidth + 'px' : 'auto');
    this.initTranslationsSubscriptions();
  }

  // required to get options this way because of the bug
  // in angular2-multiselect v8.0.0 option list gets cleared on some actions if options are provided in any other way
  protected getOptions(): MultiselectOption[] {
    return [...this.data.options];
  }

  protected selectItem(selectedItem: MultiselectOption): void {
    const selectedItems = this.data.control.value || [];
    const updatedItems = selectedItems.concat(selectedItem);
    this.data.control.setValue(updatedItems);
  }

  protected deselectItem(deselectedItem: MultiselectOption): void {
    const selectedItems = this.data.control.value;
    const updatedItems = selectedItems.filter(item => item.id !== deselectedItem.id);
    this.data.control.setValue(updatedItems);
  }

  protected selectAllItems(selectedItems: MultiselectOption[]): void {
    this.data.control.setValue(selectedItems);
  }

  protected deselectAllItems(deselectedItems: MultiselectOption[]): void {
    this.data.control.setValue(deselectedItems);
  }

  protected rotateArrowUpwards(): void {
    setTimeout(() => {
      this.arrowDownwards = false;
      this.arrowUpwards = true;
      this.cdr.markForCheck();
    });
  }

  protected rotateArrowDownwards(): void {
    setTimeout(() => {
      this.arrowUpwards = false;
      this.arrowDownwards = true;
      this.cdr.markForCheck();
    });
  }

  private initTranslationsSubscriptions(): void {
    zip([
      this.placeholder
        ? this.translocoService.selectTranslate(this.placeholder).pipe(tap(v => this.settings.text = v + '...'))
        : this.translocoService.selectTranslate('action.select').pipe(tap(v => this.settings.text = v)),
      this.translocoService.selectTranslate('action.selectAll').pipe(tap(v => this.settings.selectAllText = v)),
      this.translocoService.selectTranslate('action.deselectAll').pipe(tap(v => this.settings.unSelectAllText = v)),
      this.translocoService.selectTranslate('action.selectAllFiltered').pipe(tap(v => this.settings.filterSelectAllText = v)),
      this.translocoService.selectTranslate('action.deselectAllFiltered').pipe(tap(v => this.settings.filterUnSelectAllText = v)),
      this.translocoService.selectTranslate('action.filter').pipe(tap(v => this.settings.searchPlaceholderText = v))
    ]).subscribe(() => this.redrawComponent());
  }

  private redrawComponent(): void {
    if (this.loadedSubject.getValue()) {
      this.loadedSubject.next(false);
    }
    setTimeout(() => {
      this.loadedSubject.next(true);
    })
  }
}

export interface MultiselectOption {
  id: number;
  label: string;
}

export class MultiSelectData {
  control: FormControl<MultiselectOption[]> = new FormControl<MultiselectOption[]>([]);
  // required as readonly because of the bug
  // in angular2-multiselect v8.0.0 this list is modified on any deselect when all options are selected
  readonly options: MultiselectOption[] = [];
}
